import { useEffect, useState } from 'react';
import type { PersonKind } from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Dialog, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { ReconcileTarget } from './reconcile-target';

interface CreatePersonDialogProps {
  open: boolean;
  target: ReconcileTarget | null;
  onClose: () => void;
  onConfirmed: () => void;
}

const KINDS: PersonKind[] = ['human', 'bot', 'service'];

function initialDisplayName(target: ReconcileTarget): string {
  return target.displayName || target.realName || target.sourceAccountId;
}

async function createForSource(
  target: ReconcileTarget,
  displayName: string,
  primaryEmail: string | null,
  kind: PersonKind,
): Promise<unknown> {
  if (target.source === 'github') {
    return personApi.createAndLinkGitHub({
      login: target.sourceAccountId,
      displayName,
      primaryEmail,
      kind,
    });
  }
  return personApi.createAndLinkSlack({
    slackId: target.sourceAccountId,
    displayName,
    primaryEmail,
    kind,
  });
}

export function CreatePersonDialog({
  open,
  target,
  onClose,
  onConfirmed,
}: Readonly<CreatePersonDialogProps>) {
  const [displayName, setDisplayName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [kind, setKind] = useState<PersonKind>('human');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && target) {
      setDisplayName(initialDisplayName(target));
      setPrimaryEmail(target.email ?? '');
      setKind(target.isBot ? 'bot' : 'human');
      setError(null);
    }
  }, [open, target]);

  const handleConfirm = async (): Promise<void> => {
    if (!target) return;
    setSubmitting(true);
    setError(null);
    try {
      await createForSource(
        target,
        displayName.trim(),
        primaryEmail.trim() === '' ? null : primaryEmail.trim(),
        kind,
      );
      onConfirmed();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to create Person'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Create new Person">
      {target && (
        <div className="mb-3 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
          Linking <span className="font-mono text-foreground">{target.sourceAccountId}</span>
          <span className="ml-2 uppercase text-[10px] tracking-wide">({target.source})</span>
        </div>
      )}

      <div className="space-y-3 text-sm">
        <div>
          <label htmlFor="cpd-name" className="mb-1 block font-medium">
            Display name
          </label>
          <input
            id="cpd-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2"
          />
        </div>

        <div>
          <label htmlFor="cpd-email" className="mb-1 block font-medium">
            Primary email <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="cpd-email"
            type="email"
            value={primaryEmail}
            onChange={(e) => setPrimaryEmail(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2"
            placeholder="leave blank for none"
          />
        </div>

        <div>
          <label htmlFor="cpd-kind" className="mb-1 block font-medium">Kind</label>
          <select
            id="cpd-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as PersonKind)}
            className="w-full rounded-md border bg-background px-3 py-2"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-error-700">{error}</p>}

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} isLoading={submitting} disabled={submitting || displayName.trim() === ''}>
          Create and link
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
